import { Controller } from "@hotwired/stimulus"

// Mobile-only slide-up video popover (docs/features/v1/video-player.md, Phase 4).
//
// Desktop is unchanged: the search-grid card link is a normal full-page nav to
// the watch page (copying YouTube web). Below the `md` breakpoint a tap opens a
// bottom sheet instead. On tap we start the slide-up animation AND fetch the
// watch page concurrently, so the YouTube iframe loads under the motion and the
// open feels instant. The URL is synced via pushState so back/forward and shared
// deep-links behave (a cold deep-link just loads the full watch page).
//
// Content comes from the server-rendered watch page (single source of truth for
// the player + the Phase 3 resume handoff); we extract its `#video_player` node.
export default class extends Controller {
  static targets = ["sheet", "panel", "content", "title", "channel", "handle"]
  static values = { open: { type: Boolean, default: false } }

  connect() {
    this.boundClick = this.handleClick.bind(this)
    this.boundPopState = this.handlePopState.bind(this)
    this.boundBeforeCache = this.forceClose.bind(this)
    this.boundTouchStart = this.handleTouchStart.bind(this)
    this.boundTouchMove = this.handleTouchMove.bind(this)
    this.boundTouchEnd = this.handleTouchEnd.bind(this)
    this.boundSuppressClick = this.suppressClick.bind(this)
    this.element.addEventListener("click", this.boundClick)
    // Swipe-down to close. The YouTube iframe cannot wear a catcher (their
    // terms forbid anything in front of the player), so the gesture starts on
    // our own chrome: the title bar, the poster, and a downward pull on the
    // chapter list when that list is already scrolled to the top.
    this.panelTarget.addEventListener("touchstart", this.boundTouchStart, { passive: true })
    window.addEventListener("popstate", this.boundPopState)
    // Turbo caches a snapshot of the page before navigating away (and restores
    // it on back/forward). If we let it snapshot while the sheet is open, the
    // restored DOM brings back a visible, click-eating overlay that the freshly
    // connected controller (openValue defaults false) never hides. Force the
    // sheet shut synchronously before any snapshot is taken.
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
  }

  disconnect() {
    this.element.removeEventListener("click", this.boundClick)
    window.removeEventListener("popstate", this.boundPopState)
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    this.panelTarget.removeEventListener("touchstart", this.boundTouchStart)
    this.endDragListeners()
    document.removeEventListener("click", this.boundSuppressClick, true)
    if (this.hideTimeout) clearTimeout(this.hideTimeout)
    document.body.style.overflow = ""
  }

  // Delegated: only intercept a primary click on a watch link, on mobile, for an
  // embeddable video. Anything else falls through to normal navigation.
  handleClick(event) {
    if (event.defaultPrevented) return
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const link = event.target.closest('a[href*="/watch/"]')
    if (!link) return
    if (!this.isMobile()) return // desktop: let the link navigate

    const card = link.closest(".search-result")
    if (card && card.dataset.embeddable === "false") return // not embeddable → link out

    event.preventDefault()
    // The card's thumbnail is already loaded/cached; reuse it as an instant
    // poster so the sheet isn't black while the iframe loads.
    const thumb = card && card.querySelector(".search-result__thumbnail-image")
    this.openPopover(link.href, thumb ? thumb.currentSrc || thumb.src : null, {
      title: card ? card.dataset.title : null,
      channel: card ? card.dataset.channel : null
    })
  }

  isMobile() {
    return window.matchMedia("(max-width: 767px)").matches
  }

  // Start the slide and the fetch at the same moment (the motion masks the load).
  openPopover(url, thumbnailSrc = null, meta = {}) {
    this.thumbnailSrc = thumbnailSrc
    // Set the top-bar title/channel before the slide so it's correct as it rises.
    this.setHeader(meta.title, meta.channel)
    this.showSheet()
    this.showThumbnailPlaceholder()
    this.pushHistoryEntry()
    this.loadPlayer(url)
  }

  // Populate the top-bar title + channel; hide the channel line when absent so it
  // doesn't leave an empty gap.
  setHeader(title, channel) {
    if (this.hasTitleTarget) this.titleTarget.textContent = title || ""
    if (this.hasChannelTarget) {
      this.channelTarget.textContent = channel || ""
      this.channelTarget.classList.toggle("hidden", !channel)
    }
  }

  // Show the cached thumbnail immediately (before the fetch returns) so the
  // sliding sheet reveals the poster, not a black box.
  showThumbnailPlaceholder() {
    if (!this.thumbnailSrc) return
    const wrap = document.createElement("div")
    wrap.className = "relative w-full aspect-[16/9] bg-black"
    wrap.appendChild(this.buildThumbnail())
    this.contentTarget.replaceChildren(wrap)
  }

  buildThumbnail() {
    const img = document.createElement("img")
    img.src = this.thumbnailSrc
    img.className = "absolute inset-0 w-full h-full object-cover"
    img.setAttribute("aria-hidden", "true")
    return img
  }

  showSheet() {
    if (this.hideTimeout) {
      // Re-opening before a previous close finished animating: cancel the pending
      // hide so it can't strip the player out from under us.
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
    this.clearPanelDrag()
    document.body.style.overflow = "hidden"
    // Reveal at the start state: panel fully below the screen. The sheet only
    // captures taps once it's actually open — `pointer-events-none` is the closed
    // state, removed here and re-added the instant we close.
    this.sheetTarget.classList.remove("hidden", "pointer-events-none")
    // Force a synchronous reflow so the browser PAINTS that start state before we
    // change it — otherwise the class removal below collapses into the same frame
    // and the panel appears already part-way up instead of rising from the bottom.
    void this.panelTarget.offsetHeight
    // Slide the (opaque) panel up to cover the screen. No backdrop fade — the
    // slide itself covers the page.
    this.panelTarget.classList.remove("translate-y-full")
    this.openValue = true
  }

  hideSheet() {
    // "Closed" is deterministic and immediate: the transparent z-50 sheet stops
    // capturing taps right now (pointer-events-none), even though it stays in the
    // layout for the slide-down. We do NOT rely on the timer below to make the
    // page interactive again — only to tidy up the (now inert) layer afterwards.
    // A swipe leaves an inline translate; animate from there to off-screen
    // instead of letting the class jump the panel back to the top first.
    if (this.panelTarget.style.transform) {
      this.panelTarget.style.transition = "transform 500ms ease-in-out"
      this.panelTarget.style.transform = "translateY(100%)"
    }
    this.panelTarget.classList.add("translate-y-full")
    this.sheetTarget.classList.add("pointer-events-none")
    document.body.style.overflow = ""
    this.openValue = false
    // After the slide-down finishes, fully hide the layer and clear the player so
    // the iframe stops and audio dies. Must match the panel's transition duration.
    if (this.hideTimeout) clearTimeout(this.hideTimeout)
    this.hideTimeout = setTimeout(() => {
      this.hideTimeout = null
      this.clearPanelDrag()
      this.sheetTarget.classList.add("hidden")
      this.contentTarget.replaceChildren()
    }, 500)
  }

  // Synchronous, animation-free teardown. Used before Turbo snapshots the page so
  // a cached/restored DOM can never hold an open, click-swallowing overlay.
  forceClose() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
    this.endDragListeners()
    this.clearPanelDrag()
    this.panelTarget.classList.add("translate-y-full")
    this.sheetTarget.classList.add("hidden", "pointer-events-none")
    this.contentTarget.replaceChildren()
    document.body.style.overflow = ""
    this.openValue = false
    this.pushed = false
  }

  async loadPlayer(url) {
    try {
      const response = await fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      if (!response.ok) return this.fallbackToNav(url)

      const doc = new DOMParser().parseFromString(await response.text(), "text/html")
      const player = doc.querySelector("#video_player")
      if (!player) return this.fallbackToNav(url)

      // The watch page's <body> carries the playbacks URL the player POSTs to;
      // the search page (application layout) doesn't, so copy it before injecting.
      const playbacksUrl = doc.body.dataset.playbacksUrl
      if (playbacksUrl && !document.body.dataset.playbacksUrl) {
        document.body.dataset.playbacksUrl = playbacksUrl
      }

      if (!this.openValue) return // closed during the fetch

      // Importing + inserting connects the segment-player controller → iframe
      // loads. The fetched fragment carries its own poster (an img over the
      // stage); the segment-player keeps it until playback actually starts
      // (PLAYING) and removes it then, so the iframe loading state is never seen.
      // It matches the instant placeholder above (same thumbnail), so the swap is
      // seamless.
      this.contentTarget.replaceChildren(document.importNode(player, true))
    } catch (error) {
      this.fallbackToNav(url)
    }
  }

  fallbackToNav(url) {
    window.location.href = url
  }

  // Push a history entry WITHOUT changing the URL, purely so the hardware back
  // button closes the popover. We deliberately do NOT pushState the watch URL:
  // Turbo asynchronously rewrites history around any manual URL change, which
  // races close() non-deterministically. The watch URL stays shareable on its
  // own — a cold visit to it opens the full page. (Track "did we push" on the
  // instance, not history.state, which Turbo also clobbers.)
  pushHistoryEntry() {
    history.pushState({ videoPopover: true }, "")
    this.pushed = true
  }

  close(event) {
    if (event) event.preventDefault()
    if (!this.openValue) return

    if (this.pushed) {
      // Pop our state entry; popstate → handlePopState closes the sheet. The URL
      // never changed, so there is nothing to race.
      this.pushed = false
      history.back()
    } else {
      this.hideSheet()
    }
  }

  backdropClick(event) {
    if (event.target === event.currentTarget) this.close(event)
  }

  // --- Swipe down to close -------------------------------------------------
  // Same outcome as the close button: the sheet follows the finger, and a far
  // enough drag or a downward flick dismisses it. A short drag snaps back.

  handleTouchStart(event) {
    if (event.touches.length !== 1) return
    const kind = this.dismissGestureFrom(event.target)
    if (!kind) return

    const point = this.touchPoint(event)
    this.drag = {
      kind,
      startY: point.y,
      startX: point.x,
      lastY: point.y,
      lastTime: point.time,
      velocity: 0,
      active: false
    }
    this.panelTarget.addEventListener("touchmove", this.boundTouchMove, { passive: false })
    this.panelTarget.addEventListener("touchend", this.boundTouchEnd)
    this.panelTarget.addEventListener("touchcancel", this.boundTouchEnd)
  }

  handleTouchMove(event) {
    if (!this.drag) return
    const point = this.touchPoint(event)
    const dy = point.y - this.drag.startY
    const dx = point.x - this.drag.startX
    const dt = Math.max(point.time - this.drag.lastTime, 1)
    this.drag.velocity = (point.y - this.drag.lastY) / dt
    this.drag.lastY = point.y
    this.drag.lastTime = point.time

    if (!this.drag.active) {
      if (Math.abs(dy) < 12 && Math.abs(dx) < 12) return
      // A sideways move, or an upward one, belongs to the chapter list.
      if (Math.abs(dx) > Math.abs(dy) || dy < 0) {
        this.endDragListeners()
        this.drag = null
        return
      }
      if (this.drag.kind === "overscroll" && !this.listAtTop(event.target)) {
        this.endDragListeners()
        this.drag = null
        return
      }
      this.drag.active = true
    }

    event.preventDefault()
    this.panelTarget.style.transition = "none"
    this.panelTarget.style.transform = `translateY(${Math.max(dy, 0)}px)`
  }

  handleTouchEnd() {
    const drag = this.drag
    this.endDragListeners()
    this.drag = null
    if (!drag || !drag.active) return

    // The finger lift would otherwise click whatever is underneath (a chapter
    // row seeks; the poster starts playback).
    this.armClickSuppress()

    const dy = drag.lastY - drag.startY
    const flung = drag.velocity > 0.5 && dy > 48
    const far = dy > Math.min(160, window.innerHeight * 0.22)
    if (flung || far) {
      this.close()
    } else {
      this.snapSheetBack()
    }
  }

  // Title bar, the poster / loading still (our own elements, not the iframe),
  // or the chapter list when it cannot scroll any further up.
  dismissGestureFrom(target) {
    if (!this.openValue || !this.isMobile()) return null
    if (target.closest("iframe")) return null
    if (target.closest('[data-action*="video-popover#close"]')) return null
    if (target.closest('[data-segment-player-target="scrubber"]')) return null
    if (target.closest("a")) return null
    if (this.hasHandleTarget && this.handleTarget.contains(target)) return "sheet"
    if (target.closest('[data-segment-player-target="poster"]')) return "sheet"
    if (target.closest("[data-video-popover-target='content'] img")) return "sheet"

    const list = target.closest("#video_player ul")
    if (list && list.contains(target) && list.scrollTop <= 0) return "overscroll"
    return null
  }

  listAtTop(target) {
    const list = target.closest("#video_player ul")
    return !list || list.scrollTop <= 0
  }

  touchPoint(event) {
    const touch = event.touches[0] || event.changedTouches[0]
    return { x: touch.clientX, y: touch.clientY, time: event.timeStamp }
  }

  snapSheetBack() {
    const panel = this.panelTarget
    panel.style.transition = "transform 200ms ease-out"
    panel.style.transform = "translateY(0px)"
    const done = () => {
      panel.removeEventListener("transitionend", done)
      if (this.openValue) this.clearPanelDrag()
    }
    panel.addEventListener("transitionend", done)
  }

  clearPanelDrag() {
    this.panelTarget.style.transition = ""
    this.panelTarget.style.transform = ""
  }

  endDragListeners() {
    this.panelTarget.removeEventListener("touchmove", this.boundTouchMove)
    this.panelTarget.removeEventListener("touchend", this.boundTouchEnd)
    this.panelTarget.removeEventListener("touchcancel", this.boundTouchEnd)
  }

  armClickSuppress() {
    document.addEventListener("click", this.boundSuppressClick, true)
    // A drag that ends over empty space never produces a click. Drop the
    // one-shot listener so the next real tap is not swallowed.
    setTimeout(() => {
      document.removeEventListener("click", this.boundSuppressClick, true)
    }, 400)
  }

  suppressClick(event) {
    event.preventDefault()
    event.stopPropagation()
    document.removeEventListener("click", this.boundSuppressClick, true)
  }

  // Hardware back/forward (or our own history.back) → close the sheet. We stay on
  // the search page (never unmounted), so its scroll position is preserved.
  handlePopState() {
    if (!this.openValue) return
    this.pushed = false
    this.hideSheet()
  }
}
